/**
 * MarkBase Captcha Plugin — хук для капчи с поддомена captcha.markbase.ru
 * Провайдеры (настраиваются на стороне плагина): custom (SVG), yandex, google
 * Fail-closed: при недоступности сервиса executeCaptcha возвращает
 * { captcha_unavailable: true }; форма не отправляется, поля сохраняются, пользователь повторяет.
 *
 * Использование:
 *   const { executeCaptcha, resetCaptcha, isReady, isExecuting, isEnabled } = useCaptcha({ action: 'login' });
 *   const captchaResult = await executeCaptcha();
 *   if (captchaResult?.captcha_unavailable) { show retry; return; }
 *   await api.post('/endpoint', { ...values, ...(captchaResult || {}) });
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { devLogApiError } from 'utils/devLogApiError';

const CAPTCHA_URL_DEFAULT = (process.env.REACT_APP_CAPTCHA_URL || 'https://captcha.markbase.ru').replace(/\/+$/, '');
const CAPTCHA_SITE_KEY_ENV = process.env.REACT_APP_CAPTCHA_SITE_KEY || '';
const CAPTCHA_ENABLED = process.env.REACT_APP_CAPTCHA_ENABLED !== 'false';
const CAPTCHA_PROVIDER = process.env.REACT_APP_CAPTCHA_PROVIDER || 'custom';
const IS_DEV = process.env.NODE_ENV === 'development';

/** Безопасный разбор JSON из fetch Response (единый контракт с плагином / бэкендом). */
async function parseJsonResponse(resp) {
  try {
    const text = await resp.text();
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

// Конфиг с бэкенда (одно место для всех проектов докера)
let runtimeConfigPromise = null;
const fetchCaptchaConfig = () => {
  if (runtimeConfigPromise) return runtimeConfigPromise;
  const defaults = { captchaUrl: CAPTCHA_URL_DEFAULT, siteKey: '', provider: CAPTCHA_PROVIDER || 'custom' };
  runtimeConfigPromise = (async () => {
    try {
      const r = await fetch('/api/admin/public/captcha-config', { credentials: 'include' });
      const data = await parseJsonResponse(r);
      if (!r.ok || (typeof data.success === 'boolean' && data.success === false)) {
        return defaults;
      }
      return {
        captchaUrl: (data.captcha_url || CAPTCHA_URL_DEFAULT).replace(/\/+$/, ''),
        siteKey: (data.site_key || '').trim(),
        provider: (data.provider || CAPTCHA_PROVIDER || 'custom').trim(),
      };
    } catch {
      return defaults;
    }
  })();
  return runtimeConfigPromise;
};

// ─── Singleton-состояние (один скрипт на всё приложение) ───
let widgetScriptLoaded = false;
let widgetScriptLoading = false;
const scriptLoadCallbacks = [];

// ─── Загрузка скрипта MBCaptcha виджета ───
const loadWidgetScript = (baseUrl) => {
  const url = (baseUrl || CAPTCHA_URL_DEFAULT).replace(/\/+$/, '');
  return new Promise((resolve, reject) => {
    if (widgetScriptLoaded && window.MBCaptcha) {
      resolve();
      return;
    }
    if (widgetScriptLoading) {
      scriptLoadCallbacks.push({ resolve, reject });
      return;
    }

    widgetScriptLoading = true;
    const script = document.createElement('script');
    script.src = `${url}/widget.js`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      widgetScriptLoaded = true;
      widgetScriptLoading = false;
      if (IS_DEV) console.log('✅ MBCaptcha widget loaded');
      resolve();
      scriptLoadCallbacks.forEach(cb => cb.resolve());
      scriptLoadCallbacks.length = 0;
    };

    script.onerror = () => {
      widgetScriptLoading = false;
      const error = new Error('Не удалось загрузить скрипт виджета MBCaptcha');
      if (IS_DEV) console.warn('⚠️ MBCaptcha widget unavailable, will use direct API fallback');
      reject(error);
      scriptLoadCallbacks.forEach(cb => cb.reject(error));
      scriptLoadCallbacks.length = 0;
    };

    document.head.appendChild(script);
  });
};

// P2-2: primary color — theme или CSS-переменные (core-standards: не хардкод #1890ff)
const getPrimaryColor = () => {
  if (typeof document === 'undefined') return '#0284C7';
  const root = document.documentElement;
  return getComputedStyle(root).getPropertyValue('--ant-color-primary')?.trim() ||
    getComputedStyle(root).getPropertyValue('--mb-captcha-primary')?.trim() ||
    '#0284C7';
};

// ─── Модальное окно для Custom-капчи (DOM-based) ───
function showCustomCaptchaModal(challenge, baseUrl, siteKey) {
  const captchaUrl = baseUrl || CAPTCHA_URL_DEFAULT;
  const siteKeyVal = siteKey || CAPTCHA_SITE_KEY_ENV;
  const primary = getPrimaryColor();
  return new Promise((resolve) => {
    // Overlay
    const overlay = document.createElement('div');
    overlay.className = 'mb-captcha-overlay';
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.45); z-index: 10000;
      display: flex; align-items: center; justify-content: center;
      animation: mbCaptchaFadeIn 0.2s ease;
    `;

    // Modal
    const modal = document.createElement('div');
    modal.className = 'mb-captcha-modal';
    modal.style.cssText = `
      background: #fff; border-radius: 12px; padding: 28px 24px 20px;
      max-width: 380px; width: 92%; box-shadow: 0 12px 40px rgba(0,0,0,0.18);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      animation: mbCaptchaSlideUp 0.25s ease;
    `;

    // Inject animation keyframes (once)
    if (!document.getElementById('mb-captcha-styles')) {
      const style = document.createElement('style');
      style.id = 'mb-captcha-styles';
      style.textContent = `
        @keyframes mbCaptchaFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes mbCaptchaSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .mb-captcha-input:focus { border-color: var(--mb-captcha-primary, #0284C7) !important; outline: none; box-shadow: 0 0 0 2px rgba(2,132,199,0.15); }
        .mb-captcha-btn-submit:hover { filter: brightness(0.92); }
        .mb-captcha-btn-cancel:hover { background: #f5f5f5 !important; }
        .mb-captcha-btn-refresh:hover { background: #f0f0f0 !important; }
      `;
      document.head.appendChild(style);
    }

    const imageSrc = challenge.image || '';
    const hint = challenge.hint || 'Введите символы с картинки';
    const maxAttempts = challenge.max_attempts || 3;
    const ttl = challenge.ttl_seconds || 300;

    modal.innerHTML = `
      <div style="text-align:center; margin-bottom:16px;">
        <div style="display:inline-flex; align-items:center; gap:8px; margin-bottom:8px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${primary}" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <span style="font-size:16px; font-weight:600; color:#262626;">Проверка безопасности</span>
        </div>
        <p style="margin:0; color:#8c8c8c; font-size:13px;">${hint}</p>
      </div>
      <div style="text-align:center; margin-bottom:16px; position:relative;">
        <img src="${imageSrc}" alt="captcha" style="max-width:100%; border-radius:8px; border:1px solid #f0f0f0; background:#fafafa;" />
        <button id="mb-captcha-refresh" class="mb-captcha-btn-refresh" title="Обновить капчу"
          style="position:absolute; top:4px; right:4px; width:28px; height:28px; border-radius:50%;
                 border:1px solid #d9d9d9; background:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#595959" stroke-width="2">
            <path d="M23 4v6h-6M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
        </button>
      </div>
      <div style="margin-bottom:16px;">
        <input type="text" id="mb-captcha-input" class="mb-captcha-input" placeholder="Введите ответ"
          autocomplete="off" maxlength="10"
          style="width:100%; padding:10px 14px; border:1.5px solid #d9d9d9; border-radius:8px;
                 font-size:18px; text-align:center; letter-spacing:4px; box-sizing:border-box;
                 transition: border-color 0.2s, box-shadow 0.2s;" />
      </div>
      <div style="display:flex; gap:8px;">
        <button id="mb-captcha-cancel" class="mb-captcha-btn-cancel"
          style="flex:1; padding:9px; border:1px solid #d9d9d9; border-radius:8px; background:#fff;
                 cursor:pointer; font-size:14px; color:#595959; transition: background 0.2s;">
          Отмена
        </button>
        <button id="mb-captcha-submit" class="mb-captcha-btn-submit"
          style="flex:1; padding:9px; border:none; border-radius:8px; background:${primary};
                 color:#fff; cursor:pointer; font-size:14px; font-weight:500; transition: background 0.2s;">
          Подтвердить
        </button>
      </div>
      <div style="text-align:center; margin-top:10px; font-size:11px; color:#bfbfbf;">
        Попыток: ${maxAttempts} · Время: ${Math.floor(ttl / 60)} мин
      </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    const input = modal.querySelector('#mb-captcha-input');
    const submitBtn = modal.querySelector('#mb-captcha-submit');
    const cancelBtn = modal.querySelector('#mb-captcha-cancel');
    const refreshBtn = modal.querySelector('#mb-captcha-refresh');

    setTimeout(() => input.focus(), 100);

    const cleanup = () => {
      overlay.style.opacity = '0';
      overlay.style.transition = 'opacity 0.15s ease';
      setTimeout(() => {
        try { document.body.removeChild(overlay); } catch (_) {}
      }, 150);
    };

    const submit = () => {
      const answer = input.value.trim();
      if (!answer) {
        input.style.borderColor = 'var(--err)';
        input.focus();
        return;
      }
      cleanup();
      resolve(answer);
    };

    submitBtn.addEventListener('click', submit);
    cancelBtn.addEventListener('click', () => { cleanup(); resolve(null); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
      if (e.key === 'Escape') { cleanup(); resolve(null); }
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) { cleanup(); resolve(null); }
    });

    // Refresh: перезагрузка капчи
    refreshBtn.addEventListener('click', async () => {
      refreshBtn.disabled = true;
      try {
        const resp = await fetch(`${captchaUrl}/api/captcha/v1/challenge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ site_key: siteKeyVal, type: challenge.type || 'text', difficulty: challenge.difficulty || 'medium' })
        });
        const newChallenge = await parseJsonResponse(resp);
        if (
          resp.ok &&
          !(typeof newChallenge.success === 'boolean' && newChallenge.success === false) &&
          (newChallenge.image || newChallenge.challenge_id)
        ) {
          const img = modal.querySelector('img');
          if (img && newChallenge.image) img.src = newChallenge.image;
          challenge.challenge_id = newChallenge.challenge_id;
          input.value = '';
          input.style.borderColor = '#d9d9d9';
          input.focus();
        } else if (!resp.ok) {
          if (IS_DEV) console.warn('⚠️ Captcha refresh failed:', resp.status);
        } else if (IS_DEV) {
          console.warn('⚠️ Captcha refresh: ответ без данных или success=false');
        }
      } catch (err) {
        if (IS_DEV) console.warn('⚠️ Captcha refresh error:', err.message);
      }
      refreshBtn.disabled = false;
    });
  });
}

// ─── Верификация через MBCaptcha Widget ───
function executeWithWidget(action, widgetIdRef, siteKey, provider) {
  const siteKeyVal = siteKey || CAPTCHA_SITE_KEY_ENV;
  const providerVal = provider || CAPTCHA_PROVIDER;
  return new Promise((resolve) => {
    try {
      // Проверяем, что виджет загружен
      if (!window.MBCaptcha || typeof window.MBCaptcha.render !== 'function') {
        if (IS_DEV) console.warn('⚠️ MBCaptcha widget not loaded, falling back to direct API');
        resolve(null);
        return;
      }

      // Создаём / находим контейнер
      let container = document.getElementById('mb-captcha-widget-container');
      if (!container) {
        container = document.createElement('div');
        container.id = 'mb-captcha-widget-container';
        // Для custom — контейнер будет видим (виджет сам создаёт UI)
        // Для invisible — контейнер скрыт
        if (providerVal !== 'custom') {
          container.style.display = 'none';
        }
        document.body.appendChild(container);
      }

      // Уничтожаем предыдущий виджет
      if (widgetIdRef.current !== null) {
        try {
          if (window.MBCaptcha && typeof window.MBCaptcha.destroy === 'function') {
            window.MBCaptcha.destroy(widgetIdRef.current);
          }
        } catch (_) {}
        container.innerHTML = '';
      }

      const timeoutId = setTimeout(() => {
        resolve(null);
      }, 60000);

      let id;
      try {
        id = window.MBCaptcha.render(container, {
          siteKey: siteKeyVal,
          provider: providerVal,
          theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
          lang: 'ru',
          action: action,
          onVerify: (result) => {
            clearTimeout(timeoutId);
            widgetIdRef.current = id;

            // ✅ ИСПРАВЛЕНО: После успешной верификации скрываем контейнер виджета
            // Элемент "Проверено" больше не будет отображаться в интерфейсе
            const hideCaptchaWidget = () => {
              if (container && container.parentNode) {
                // Скрываем контейнер виджета после успешной верификации
                container.style.display = 'none';
                container.style.visibility = 'hidden';
                container.style.opacity = '0';
                container.style.pointerEvents = 'none';

                // Также скрываем все элементы успеха внутри контейнера
                const successElements = container.querySelectorAll('.mb-captcha-success, [class*="success"], [class*="verified"], [class*="mb-captcha"]');
                successElements.forEach(el => {
                  el.style.display = 'none';
                  el.style.visibility = 'hidden';
                  el.style.opacity = '0';
                  el.style.pointerEvents = 'none';
                });
              }
            };

            // Скрываем сразу и через небольшую задержку (на случай если виджет еще рендерится)
            hideCaptchaWidget();
            setTimeout(hideCaptchaWidget, 100);
            setTimeout(hideCaptchaWidget, 500);

            resolve({
              captcha_token: result.token || '',
              challenge_id: result.challengeId || '',
              captcha_answer: result.answer || result.token || '',
              captcha_site_key: siteKeyVal,
              captcha_provider: result.provider || providerVal
            });
          },
          onError: (error) => {
            clearTimeout(timeoutId);
            if (IS_DEV) console.warn('⚠️ MBCaptcha widget error:', error);
            resolve(null);
          },
          onExpire: () => {
            clearTimeout(timeoutId);
            resolve(null);
          }
        });

        widgetIdRef.current = id;
      } catch (renderError) {
        clearTimeout(timeoutId);
        if (IS_DEV) devLogApiError('капча: рендер MBCaptcha', renderError);
        resolve(null);
      }
    } catch (error) {
      if (IS_DEV) devLogApiError('капча: виджет MBCaptcha', error);
      resolve(null);
    }
  });
}

// ─── Верификация через Direct API (Custom captcha fallback) ───
async function executeWithDirectApi(action, baseUrl, siteKey) {
  const captchaUrl = baseUrl || CAPTCHA_URL_DEFAULT;
  const siteKeyVal = siteKey || CAPTCHA_SITE_KEY_ENV;
  try {
    const resp = await fetch(`${captchaUrl}/api/captcha/v1/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        site_key: siteKeyVal,
        type: 'text',
        difficulty: 'medium'
      })
    });

    const challenge = await parseJsonResponse(resp);

    if (!resp.ok) {
      if (IS_DEV) {
        const preview = Object.keys(challenge).length ? JSON.stringify(challenge).slice(0, 200) : '';
        console.warn('⚠️ Captcha challenge request failed:', resp.status, preview);
      }
      return null;
    }

    if (typeof challenge.success === 'boolean' && challenge.success === false) {
      if (IS_DEV) console.warn('⚠️ Captcha challenge: success=false');
      return null;
    }

    if (!challenge.image && !challenge.challenge_id) {
      return null;
    }

    // Показываем модальное окно с SVG-капчой
    const answer = await showCustomCaptchaModal(challenge, captchaUrl, siteKeyVal);

    if (!answer) return null; // Пользователь отменил

    return {
      captcha_token: challenge.challenge_id,
      challenge_id: challenge.challenge_id,
      captcha_answer: answer,
      captcha_site_key: siteKeyVal,
      captcha_provider: 'custom'
    };
  } catch (error) {
    // Обрабатываем CORS и другие сетевые ошибки
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      if (IS_DEV) console.warn('⚠️ Капча: CORS/сеть:', error.message);
    } else {
      if (IS_DEV) console.warn('⚠️ Капча: ошибка прямого API:', error.message);
    }
    return null;
  }
}

// ═══════════════════════════════════════
// ✅ Основной хук
// ═══════════════════════════════════════
export const useCaptcha = (options = {}) => {
  const {
    onSuccess,
    onError,
    action = 'submit',
    autoInit = true
  } = options;

  const [isReady, setIsReady] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [runtimeConfig, setRuntimeConfig] = useState(null);
  const widgetIdRef = useRef(null);

  // Ключи: из env или с бэкенда (/api/admin/public/captcha-config) для всех проектов докера
  const effectiveUrl = runtimeConfig?.captchaUrl || CAPTCHA_URL_DEFAULT;
  const effectiveSiteKey = runtimeConfig?.siteKey || CAPTCHA_SITE_KEY_ENV;
  const effectiveProvider = runtimeConfig?.provider || CAPTCHA_PROVIDER;
  const pluginConfigured = CAPTCHA_ENABLED && !!effectiveSiteKey;

  // Загрузка runtime-конфига с бэкенда: provider/site_key могут меняться без пересборки фронта.
  useEffect(() => {
    fetchCaptchaConfig().then(setRuntimeConfig);
  }, []);

  useEffect(() => {
    if (!pluginConfigured) {
      setIsReady(true);
      return;
    }

    let mounted = true;

    const init = async () => {
      if (!autoInit) return;
      try {
        await loadWidgetScript(effectiveUrl);
      } catch (_) {
        // Виджет может не загрузиться; executeCaptcha попробует direct API.
      }
      if (mounted) setIsReady(true);
    };

    init();
    return () => { mounted = false; };
  }, [autoInit, pluginConfigured, effectiveUrl]);


  // ─── Выполнить капчу ───
  const executeCaptcha = useCallback(async () => {
    if (!pluginConfigured) {
      return CAPTCHA_ENABLED ? { captcha_unavailable: true } : null;
    }

    setIsExecuting(true);

    try {
      if (window.MBCaptcha) {
        const result = await executeWithWidget(action, widgetIdRef, effectiveSiteKey, effectiveProvider);
        if (result) {
          if (onSuccess) onSuccess(result.captcha_token);
          return result;
        }
      }

      if (effectiveProvider === 'custom') {
        const result = await executeWithDirectApi(action, effectiveUrl, effectiveSiteKey);
        if (result) {
          if (onSuccess) onSuccess(result.captcha_token);
          return result;
        }
      }

      if (IS_DEV) console.warn('⚠️ Captcha unavailable');
      return { captcha_unavailable: true };
    } catch (error) {
      if (IS_DEV) devLogApiError('капча: executeCaptcha', error);
      if (onError) onError(error);
      return { captcha_unavailable: true };
    } finally {
      setIsExecuting(false);
    }
  }, [action, pluginConfigured, effectiveUrl, effectiveSiteKey, effectiveProvider, onSuccess, onError]);

  // ─── Сброс капчи ───
  const resetCaptcha = useCallback(() => {
    try {
      if (window.MBCaptcha && widgetIdRef.current !== null) {
        window.MBCaptcha.reset(widgetIdRef.current);
      }

      // ✅ ИСПРАВЛЕНО: При сбросе капчи показываем контейнер обратно (если он был скрыт)
      const container = document.getElementById('mb-captcha-widget-container');
      if (container && effectiveProvider === 'custom') {
        container.style.display = '';
      }
    } catch (error) {
      if (IS_DEV) console.warn('⚠️ Captcha reset error:', error);
    }
  }, [effectiveProvider]);

  return {
    executeCaptcha,
    resetCaptcha,
    isReady,
    isExecuting,
    isEnabled: pluginConfigured
  };
};

export default useCaptcha;
